/* eslint-disable react/require-default-props */
import React from 'react';
import proptypes from 'prop-types';
import { Promotion } from '@thd-olt-component-react/promotion';
import { extend } from '@thd-nucleus/data-sources';
import { convertUtcToPacificAndFormat } from '../utils/promotion-date-utils';

const { shape, string, arrayOf } = proptypes;

/**
 * Transforms priceAdjustment API data into the format expected by the Promotion component
 * Specifically handles SBOTD/SBOTW promotion data from bundle-level price adjustments
 *
 * @param {object} priceAdjustment - Price adjustment data from usePriceAdjustment hook
 * @returns {object|null} - Transformed promotion data or null if no special buy promotion found
 */
const transformPriceAdjustmentToPromotionData = (priceAdjustment) => {
  if (!priceAdjustment?.promotions?.length) {
    return null;
  }

  // Look for SBOTD/SBOTW promotions in the cart response promotions array
  // Check for "special buy of the day" or "special buy of the week" (case-insensitive)
  const sbotdSbotwerPromo = priceAdjustment.promotions
    .find((promotion) => {
      const shortDesc = promotion.shortDesc?.toLowerCase() || '';
      return shortDesc.includes('special buy of the day') || shortDesc.includes('special buy of the week');
    });

  if (!sbotdSbotwerPromo) {
    return null;
  }

  // Transform the API data into the format expected by the Promotion component
  return {
    product: {
      pricing: {
        promotion: {
          description: {
            shortDesc: sbotdSbotwerPromo.shortDesc,
            longDesc: sbotdSbotwerPromo.longDesc
          },
          dates: {
            start: convertUtcToPacificAndFormat(sbotdSbotwerPromo?.startDt),
            end: convertUtcToPacificAndFormat(sbotdSbotwerPromo?.endDt)
          }
        }
      }
    }
  };
};

/**
 * BundlePromotion component for displaying SBOTD/SBOTW countdown timers on BIP pages
 * This component acts as a bridge between the BIP's priceAdjustment data and the Promotion component
 *
 * Props (provided by withBundleProducts HOC):
 * @param {string} itemId - The bundle item ID (required for Promotion component)
 * @param {object} pricingDetails - Pricing details from useBundlePricingDetails hook
 */
const BundlePromotion = ({
  itemId,
  pricingDetails = null
}) => {
  // Transform the priceAdjustment data into the format expected by the Promotion component
  const bipPromotionData = transformPriceAdjustmentToPromotionData(pricingDetails?.adjustments);

  // Only render if we have valid promotion data (real or mock)
  if (!bipPromotionData) {
    return null;
  }

  return (
    <Promotion
      itemId={itemId}
      promotionData={bipPromotionData}
    />
  );
};

BundlePromotion.displayName = 'BundlePromotion';

BundlePromotion.propTypes = {
  /** The bundle item ID */
  itemId: string.isRequired,
  /** Pricing details from useBundlePricingDetails hook (includes adjustments) */
  pricingDetails: shape({
    adjustments: shape({
      promotions: arrayOf(shape({}))
    })
  })
};

BundlePromotion.dataModel = extend(Promotion);

export { BundlePromotion };
